import { API_URL } from '@/config';
import { PlatformerLevel } from '../../shared/CharacterTypes';
import { LevelFormatConverter } from '../../shared/LevelFormatConverter';
import { serializeGridLevel } from '../../shared/TileEditorTypes';
import { LevelRegistry } from '../systems/map/LevelRegistry';
import { LevelStorageService, SavedMapInfo } from './LevelStorageService';
import { storage } from './StorageService';
import { gameLogger } from '../../utils/GameLogger';

import { ILevelProvider } from '../../shared/interfaces/ILevelProvider';

/**
 * Service for handling level persistence and API communication
 * Manages saving/loading levels from storage and API
 */
export class LevelService implements ILevelProvider {
  private static instance: LevelService;
  private levelStorage: LevelStorageService;

  private constructor() {
    this.levelStorage = LevelStorageService.getInstance();
  }

  public static getInstance(): LevelService {
    if (!LevelService.instance) {
      LevelService.instance = new LevelService();
    }
    return LevelService.instance;
  }

  private mapCache: SavedMapInfo[] = [];

  /**
   * Get list of all available levels (built-in + custom saved)
   * Returns cached maps immediately. Call fetchAvailableMaps to refresh.
   */
  public getAvailableMaps(): SavedMapInfo[] {
    if (this.mapCache.length === 0) {
      // Return local maps only as an immediate fallback
      return this.levelStorage.getAvailableMaps();
    }
    return this.mapCache;
  }

  /**
   * Fetch all available maps from and API and merge with local storage
   */
  public async fetchAvailableMaps(): Promise<SavedMapInfo[]> {
    const localMaps = this.levelStorage.getAvailableMaps();
    const apiMaps: SavedMapInfo[] = [];

    try {
      gameLogger.info('level', `[LevelService] Fetching map list from API: ${API_URL}/levels`);
      const response = await fetch(`${API_URL}/levels`);
      if (response.ok) {
        const rawMaps = await response.json();
        // Handle both Array and wrapped data
        const maps = Array.isArray(rawMaps) ? rawMaps : (rawMaps.data || []);

        maps.forEach((m: any) => {
          apiMaps.push({
            id: m.id || m._id,
            name: m.name || 'Untitled Map',
            savedAt: m.updatedAt ? new Date(m.updatedAt).getTime() : Date.now(),
            isBuiltIn: false // API maps are always custom/uploaded
          });
        });
        gameLogger.info('level', `📥 [LevelService] Fetched ${apiMaps.length} maps from API`);
      }
    } catch (e) {
      gameLogger.warn('level', '⚠️ [LevelService] Could not fetch maps from API:', e);
    }

    // Merge and deduplicate by ID (Local Priority)
    const merged = [...localMaps];
    apiMaps.forEach(apiMap => {
      if (!merged.find(m => m.id === apiMap.id)) {
        merged.push(apiMap);
      }
    });

    this.mapCache = merged;
    return merged;
  }


  /**
   * Save current level to localStorage
   * Returns the saved level ID (may be different if it was a built-in level)
   */
  public saveLevel(level: PlatformerLevel): string | null {
    return this.levelStorage.saveLevel(level);
  }

  /**
   * Save to test map workspace
   */
  public saveToTestMap(level: PlatformerLevel): boolean {
    return this.levelStorage.saveToTestMap(level);
  }

  /**
   * Load the dev-map workspace if it exists
   */
  public getDevMap(): PlatformerLevel | null {
    return this.levelStorage.getDevMap();
  }

  /**
   * Load level from storage or API
   * First tries localStorage, then falls back to API
   */
  public async loadLevel(levelId: string): Promise<PlatformerLevel | null> {
    gameLogger.info('level', `[LevelService] Attempting to load level: ${levelId}`);

    // First, try to load from localStorage (custom saved levels)
    const storedLevel = this.levelStorage.loadLevel(levelId);
    if (storedLevel) {
      gameLogger.info('level', `[LevelService] Loaded custom level from storage: ${levelId}`);
      gameLogger.info('level', `[LevelService] Level has ${storedLevel.entities?.length || 0} entities, isGridLevel=${LevelFormatConverter.isGridLevel(storedLevel)}`);

      // Check if it's a GridLevel (editor format) or PlatformerLevel (game format)
      if (LevelFormatConverter.isGridLevel(storedLevel)) {
        gameLogger.info('level', `[LevelService] Converting GridLevel to PlatformerLevel: ${levelId}`);
        const converted = LevelFormatConverter.gridToPlatformer(storedLevel);
        gameLogger.info('level', `[LevelService] Converted: ${converted.entities?.length || 0} entities`);
        return converted;
      } else {
        // Already a PlatformerLevel
        gameLogger.info('level', `[LevelService] Returning PlatformerLevel as-is: ${storedLevel.entities?.length || 0} entities`);
        return storedLevel as PlatformerLevel;
      }
    }

    // Check if it's a built-in level from LevelRegistry
    const registry = LevelRegistry.getInstance();
    const registeredLevel = registry.getLevel(levelId);
    if (registeredLevel) {
      gameLogger.info('level', `[LevelService] Loaded built-in level from registry: ${levelId}`);
      return registeredLevel;
    }

    // Try to load from API asynchronously
    gameLogger.info('level', `[LevelService] Level not in storage or registry, trying API: ${levelId}`);
    const apiLevel = await this.fetchLevelFromApi(levelId);
    if (apiLevel) {
      return apiLevel;
    }

    gameLogger.warn('level', `[LevelService] Level not found anywhere: ${levelId}`);
    return null;
  }

  /**
   * Fetch level data from API
   */
  private async fetchLevelFromApi(levelId: string): Promise<PlatformerLevel | null> {
    try {
      // Create a timeout promise
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      gameLogger.info('level', `[LevelService] Fetching level ${levelId} from API: ${API_URL}/levels/${levelId}`);

      try {
        const response = await fetch(`${API_URL}/levels/${levelId}`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        if (response.ok) {
          const levelData = await response.json();
          gameLogger.info('level', `📥 [LevelService] Loaded level ${levelId} from API`);

          if (levelData && !levelData.platforms && levelData.data) {
            gameLogger.warn('level', '[LevelService] API returned nested data structure, unwrapping');
            return levelData.data;
          }

          return levelData;
        }
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          gameLogger.warn('level', `[LevelService] Fetch timed out for level ${levelId}`);
        } else {
          throw fetchError; // Re-throw to outer catch
        }
      }
    } catch (e) {
      gameLogger.warn('level', `⚠️ [LevelService] Could not load level ${levelId} from API:`, e);
    }
    return null;
  }

  /**
   * Save level to API
   */
  public async saveLevelToApi(level: PlatformerLevel): Promise<boolean> {
    try {
      const token = storage.get('helix_jwt');
      const response = await fetch(`${API_URL}/levels/${level.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(level),
      });

      if (response.ok) {
        gameLogger.info('level', '✅ [LevelService] Level saved successfully to API');
        return true;
      } else {
        gameLogger.error('level', '❌ [LevelService] Failed to save level to API:', await response.text());
        return false;
      }
    } catch (e) {
      gameLogger.error('level', '❌ [LevelService] Error saving level to API:', e);
      return false;
    }
  }

  /**
   * Save level to local file for development
   */
  public saveLevelToFile(level: PlatformerLevel): boolean {
    if (typeof window === 'undefined') return false;
    try {
      // SPARSE: Serialize editorGrid Maps to JSON-compatible format
      let levelToSave = level;
      if (level.editorGrid) {
        levelToSave = {
          ...level,
          editorGrid: serializeGridLevel(level.editorGrid)
        };
      }
      const dataStr = JSON.stringify(levelToSave, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `${level.id}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      gameLogger.info('level', `✅ [LevelService] Level saved to file: ${level.id}.json`);
      return true;
    } catch (e) {
      gameLogger.error('level', '❌ [LevelService] Error saving level to file:', e);
      return false;
    }
  }
}
